import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Save, Loader2, Image as ImageIcon, Video, GripVertical, FolderOpen, Plus, Trash2,
  ChevronDown, ChevronUp, Eye, EyeOff, Italic, ExternalLink, AlertCircle, Sparkles
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import MediaPickerModal from "../../components/MediaPickerModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FIELD = "block w-full bg-white border border-[#D4859B]/20 focus:border-seikat-pink rounded-xl px-3.5 py-2 text-[#1C1A19] placeholder:text-[#A8A29A] font-body text-sm outline-none transition-colors";
const LABEL = "block text-[10px] tracking-[0.2em] uppercase text-[#8A857D] mb-1.5 font-body";

const SECTION_TEMPLATES = {
  generic: {
    eyebrow: "NEW SECTION",
    headline_id: "Judul <em>baru</em> di sini.",
    headline_en: "A <em>new</em> headline here.",
    body_id: "Tuliskan deskripsi puitis di sini…",
    body_en: "Write a poetic description here…",
    cta_label_id: "Selengkapnya",
    cta_label_en: "Learn More",
    cta_href: "/",
    media_type: "image",
    media_url: "",
    poster_url: "",
    overlay_opacity: 0.55,
    active: true,
  },
};

function insertEm(text, ref) {
  if (!ref?.current) return text;
  const ta = ref.current;
  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  const selected = text.substring(start, end) || "kata";
  return text.substring(0, start) + `<em>${selected}</em>` + text.substring(end);
}

function SortableSection({ section, idx, expanded, onToggleExpand, onUpdate, onRemove, onToggleActive, openPicker }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const headlineIdRef = useRef(null);
  const headlineEnRef = useRef(null);
  const isActive = section.active !== false;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`landing-section-${section.id}`}
      className={`bg-white border ${expanded ? "border-seikat-pink/30 shadow-[0_8px_28px_-12px_rgba(212,133,155,0.25)]" : "border-[#D4859B]/15"} rounded-2xl overflow-hidden transition-all`}
    >
      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#FDF8F6] to-white">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-seikat-pink/10 text-[#8A857D] hover:text-seikat-pink" title="Drag to reorder">
          <GripVertical size={14} />
        </button>
        <span className="font-heading text-xl text-[#1C1A19]">{String(idx + 1).padStart(2, "0")}</span>
        <div className="flex-1 min-w-0">
          <p className="font-body text-[10px] tracking-[0.25em] uppercase text-seikat-pink">{section.eyebrow || "section"}</p>
          <p className="font-body text-sm text-[#1C1A19] truncate" dangerouslySetInnerHTML={{ __html: (section.headline_id || section.headline_en || "—").replace(/<em>/g, '<em class="text-seikat-pink not-italic font-medium">') }} />
        </div>
        {/* preview thumbnail */}
        {section.media_url && (
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#FDF8F6] flex-shrink-0">
            {section.media_type === "image" ? (
              <img src={section.media_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-seikat-pink"><Video size={14} /></div>
            )}
          </div>
        )}
        <button
          onClick={() => onToggleActive(section.id)}
          title={isActive ? "Sembunyikan section" : "Aktifkan section"}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isActive ? "text-seikat-pink hover:bg-seikat-pink/10" : "text-[#A8A29A] hover:bg-[#FDF8F6]"}`}
          data-testid={`section-active-${section.id}`}
        >
          {isActive ? <Eye size={13} /> : <EyeOff size={13} />}
        </button>
        <button
          onClick={() => onRemove(section.id)}
          title="Hapus section"
          className="w-8 h-8 rounded-full hover:bg-red-50 hover:text-red-500 text-[#A8A29A] flex items-center justify-center transition-colors"
          data-testid={`section-remove-${section.id}`}
        >
          <Trash2 size={13} />
        </button>
        <button
          onClick={() => onToggleExpand(section.id)}
          className="w-8 h-8 rounded-full bg-[#FDF8F6] hover:bg-seikat-pink/10 hover:text-seikat-pink text-[#4A453F] flex items-center justify-center transition-colors"
          data-testid={`section-expand-${section.id}`}
        >
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="p-5 border-t border-[#D4859B]/10 grid lg:grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>ID Slug</label>
                <input className={FIELD} value={section.id || ""} onChange={e => onUpdate(section.id, "id", e.target.value.replace(/[^a-z0-9_-]/gi, "_"))} />
                <p className="text-[10px] text-[#A8A29A] mt-1">Identifier teknis (tanpa spasi)</p>
              </div>
              <div>
                <label className={LABEL}>Eyebrow (kapital kecil di atas judul)</label>
                <input className={FIELD} value={section.eyebrow || ""} onChange={e => onUpdate(section.id, "eyebrow", e.target.value)} />
              </div>
              {/* Headlines */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={LABEL + " !mb-0"}>🇮🇩 Headline — Indonesia</label>
                  <button type="button" onClick={() => onUpdate(section.id, "headline_id", insertEm(section.headline_id || "", headlineIdRef))} className="text-[10px] text-seikat-pink hover:underline flex items-center gap-1" title="Bungkus dengan <em>">
                    <Italic size={9} /> +italic
                  </button>
                </div>
                <textarea ref={headlineIdRef} rows={2} className={FIELD} value={section.headline_id || ""} onChange={e => onUpdate(section.id, "headline_id", e.target.value)} data-testid={`headline-id-${section.id}`} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={LABEL + " !mb-0"}>🇬🇧 Headline — English</label>
                  <button type="button" onClick={() => onUpdate(section.id, "headline_en", insertEm(section.headline_en || "", headlineEnRef))} className="text-[10px] text-seikat-pink hover:underline flex items-center gap-1">
                    <Italic size={9} /> +italic
                  </button>
                </div>
                <textarea ref={headlineEnRef} rows={2} className={FIELD} value={section.headline_en || ""} onChange={e => onUpdate(section.id, "headline_en", e.target.value)} data-testid={`headline-en-${section.id}`} />
              </div>
              <div>
                <label className={LABEL}>🇮🇩 Body — Indonesia</label>
                <textarea rows={3} className={FIELD} value={section.body_id || ""} onChange={e => onUpdate(section.id, "body_id", e.target.value)} />
              </div>
              <div>
                <label className={LABEL}>🇬🇧 Body — English</label>
                <textarea rows={3} className={FIELD} value={section.body_en || ""} onChange={e => onUpdate(section.id, "body_en", e.target.value)} />
              </div>
              <div>
                <label className={LABEL}>CTA Label — ID</label>
                <input className={FIELD} value={section.cta_label_id || ""} onChange={e => onUpdate(section.id, "cta_label_id", e.target.value)} />
              </div>
              <div>
                <label className={LABEL}>CTA Label — EN</label>
                <input className={FIELD} value={section.cta_label_en || ""} onChange={e => onUpdate(section.id, "cta_label_en", e.target.value)} />
              </div>
              <div className="lg:col-span-2">
                <label className={LABEL}>CTA Link (URL)</label>
                <input className={FIELD} value={section.cta_href || ""} onChange={e => onUpdate(section.id, "cta_href", e.target.value)} placeholder="/catalog" />
              </div>
              <div>
                <label className={LABEL}>Media Type</label>
                <select className={FIELD} value={section.media_type || "image"} onChange={e => onUpdate(section.id, "media_type", e.target.value)}>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
              </div>
              <div>
                <label className={LABEL}>Overlay opacity (0–1)</label>
                <input type="number" step="0.05" min="0" max="1" className={FIELD} value={section.overlay_opacity ?? 0.55} onChange={e => onUpdate(section.id, "overlay_opacity", parseFloat(e.target.value))} />
              </div>
              <div className="lg:col-span-2">
                <label className={LABEL}>{section.media_type === "video" ? <><Video size={11} className="inline mr-1" />Video URL</> : <><ImageIcon size={11} className="inline mr-1" />Image URL</>}</label>
                <div className="flex gap-2">
                  <input className={`${FIELD} flex-1`} value={section.media_url || ""} onChange={e => onUpdate(section.id, "media_url", e.target.value)} placeholder="https://… atau /api/uploads/…" />
                  <button type="button" onClick={() => openPicker(section.id, "media_url", section.media_type === "video" ? "video" : "image")} className="flex items-center gap-1.5 px-3.5 py-2 bg-seikat-pink/10 hover:bg-seikat-pink/20 text-seikat-pink rounded-xl text-xs font-body whitespace-nowrap" data-testid={`picker-media-${section.id}`}>
                    <FolderOpen size={12} /> Galeri
                  </button>
                </div>
                {section.media_url && (
                  <div className="mt-2 rounded-lg overflow-hidden bg-[#FDF8F6] max-h-48">
                    {section.media_type === "image" ? (
                      <img src={section.media_url} alt="" className="w-full max-h-48 object-cover" />
                    ) : (
                      <video src={section.media_url} className="w-full max-h-48 object-cover" muted controls={false} autoPlay loop />
                    )}
                  </div>
                )}
              </div>
              {section.media_type === "video" && (
                <div className="lg:col-span-2">
                  <label className={LABEL}>Poster URL (fallback image)</label>
                  <div className="flex gap-2">
                    <input className={`${FIELD} flex-1`} value={section.poster_url || ""} onChange={e => onUpdate(section.id, "poster_url", e.target.value)} placeholder="https://…" />
                    <button type="button" onClick={() => openPicker(section.id, "poster_url", "image")} className="flex items-center gap-1.5 px-3.5 py-2 bg-seikat-pink/10 hover:bg-seikat-pink/20 text-seikat-pink rounded-xl text-xs font-body whitespace-nowrap">
                      <FolderOpen size={12} /> Galeri
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AdminLanding() {
  const [data, setData] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(null); // { sectionId, field, accept }
  const [expanded, setExpanded] = useState(new Set());

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    axios.get(`${API}/cms/landing`).then(r => { setData(r.data); setInitial(JSON.stringify(r.data)); }).catch(() => toast.error("Gagal memuat landing"));
  }, []);

  const isDirty = useMemo(() => data && initial && JSON.stringify(data) !== initial, [data, initial]);

  const updateSection = (sectionId, key, value) => {
    setData(prev => ({
      ...prev,
      sections: prev.sections.map(s => s.id === sectionId ? { ...s, [key]: value } : s),
    }));
  };

  const removeSection = (sectionId) => {
    if (data.sections.length <= 1) {
      toast.error("Minimal 1 section harus ada");
      return;
    }
    if (!window.confirm("Hapus section ini?")) return;
    setData(prev => ({ ...prev, sections: prev.sections.filter(s => s.id !== sectionId) }));
    setExpanded(prev => { const n = new Set(prev); n.delete(sectionId); return n; });
  };

  const toggleActive = (sectionId) => {
    setData(prev => ({
      ...prev,
      sections: prev.sections.map(s => s.id === sectionId ? { ...s, active: s.active === false ? true : false } : s),
    }));
  };

  const toggleExpand = (sectionId) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(sectionId)) n.delete(sectionId); else n.add(sectionId);
      return n;
    });
  };

  const expandAll = () => setExpanded(new Set(data.sections.map(s => s.id)));
  const collapseAll = () => setExpanded(new Set());

  const addSection = () => {
    const id = `section_${Date.now().toString(36)}`;
    setData(prev => ({
      ...prev,
      sections: [...(prev.sections || []), { ...SECTION_TEMPLATES.generic, id, order: prev.sections?.length || 0 }],
    }));
    setExpanded(prev => new Set([...prev, id]));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setData(prev => {
      const oldIdx = prev.sections.findIndex(s => s.id === active.id);
      const newIdx = prev.sections.findIndex(s => s.id === over.id);
      const reordered = arrayMove(prev.sections, oldIdx, newIdx).map((s, i) => ({ ...s, order: i }));
      return { ...prev, sections: reordered };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...data, sections: data.sections.map((s, i) => ({ ...s, order: i })) };
      await axios.put(`${API}/admin/cms/landing`, payload, { withCredentials: true });
      setInitial(JSON.stringify(payload));
      setData(payload);
      toast.success("Landing tersimpan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Simpan gagal");
    } finally {
      setSaving(false);
    }
  };

  const openPicker = (sectionId, field, accept = "all") => setPicker({ sectionId, field, accept });
  const handlePickMedia = (url) => {
    if (!picker) return;
    const absolute = url.startsWith("http") ? url : `${BACKEND}${url}`;
    updateSection(picker.sectionId, picker.field, absolute);
  };

  if (!data) return <Loader2 className="animate-spin text-seikat-pink mx-auto mt-20" size={28} />;

  return (
    <div data-testid="admin-landing">
      {/* Header */}
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="w-8 h-px bg-seikat-pink" />
            <span className="text-[10px] tracking-[0.35em] uppercase text-seikat-pink font-body">Landing CMS</span>
          </div>
          <h1 className="font-heading text-4xl text-[#1C1A19] leading-tight">Landing <em className="text-seikat-pink">Hero</em></h1>
          <p className="font-body text-sm text-[#4A453F]/75 mt-1.5">Edit & susun ulang section landing. Drag handle di kiri untuk reorder, klik chevron untuk expand.</p>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full text-[11px] font-body text-amber-700">
              <AlertCircle size={11} /> Perubahan belum disimpan
            </span>
          )}
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-4 py-2 border border-[#D4859B]/20 rounded-full text-xs font-body text-[#4A453F] hover:bg-[#FDF8F6]" data-testid="landing-preview-link">
            <ExternalLink size={11} /> Preview
          </a>
          <button onClick={save} disabled={saving || !isDirty} className="flex items-center gap-2 px-5 py-2.5 bg-seikat-pink text-white rounded-full text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors disabled:opacity-40" data-testid="admin-landing-save">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </div>

      {/* Loading phrase */}
      <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-5 mb-6">
        <label className={LABEL}>Loading Phrase (teks saat loading screen)</label>
        <input className={FIELD} value={data.loading_phrase || ""} onChange={e => setData({ ...data, loading_phrase: e.target.value })} placeholder="Merangkai puisi alam" data-testid="admin-landing-loading-phrase" />
      </div>

      {/* Sections header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={13} className="text-seikat-pink" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#4A453F] font-body">Sections · {data.sections?.length || 0}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={expandAll} className="text-[10px] text-[#4A453F] hover:text-seikat-pink px-2 py-1">Buka semua</button>
          <span className="text-[#D4859B]/40">|</span>
          <button onClick={collapseAll} className="text-[10px] text-[#4A453F] hover:text-seikat-pink px-2 py-1">Tutup semua</button>
        </div>
      </div>

      {/* Sections list with DnD */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={data.sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {data.sections.map((s, idx) => (
              <SortableSection
                key={s.id}
                section={s}
                idx={idx}
                expanded={expanded.has(s.id)}
                onToggleExpand={toggleExpand}
                onUpdate={updateSection}
                onRemove={removeSection}
                onToggleActive={toggleActive}
                openPicker={openPicker}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add section button */}
      <button onClick={addSection} className="mt-4 w-full flex items-center justify-center gap-2 py-4 border-2 border-dashed border-[#D4859B]/25 hover:border-seikat-pink rounded-2xl text-sm font-body text-[#4A453F] hover:text-seikat-pink hover:bg-seikat-pink/5 transition-all" data-testid="add-section-btn">
        <Plus size={14} /> Tambah Section
      </button>

      <MediaPickerModal
        open={!!picker}
        onClose={() => setPicker(null)}
        onSelect={handlePickMedia}
        accept={picker?.accept || "all"}
        title={picker?.field === "poster_url" ? <>Pilih <em className="text-seikat-pink">Poster Image</em></> : null}
      />
    </div>
  );
}
